import * as React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type Variant = 'default' | 'destructive';

type AlertReq = {
  kind: 'alert';
  title?: string;
  message: React.ReactNode;
  okLabel?: string;
};

type ConfirmReq = {
  kind: 'confirm';
  title?: string;
  message: React.ReactNode;
  okLabel?: string;
  cancelLabel?: string;
  variant?: Variant;
};

type PromptReq = {
  kind: 'prompt';
  title?: string;
  message?: React.ReactNode;
  defaultValue?: string;
  placeholder?: string;
  okLabel?: string;
  cancelLabel?: string;
};

type Req = (AlertReq | ConfirmReq | PromptReq) & {
  id: number;
  resolve: (v: any) => void;
};

let nextId = 1;
const listeners = new Set<(reqs: Req[]) => void>();
let queue: Req[] = [];

function notify() {
  for (const fn of listeners) fn(queue);
}

function push(r: Omit<Req, 'id' | 'resolve'>): Promise<any> {
  return new Promise(resolve => {
    queue = [...queue, { ...r, id: nextId++, resolve } as Req];
    notify();
  });
}

function settle(id: number, value: any) {
  const item = queue.find(q => q.id === id);
  if (!item) return;
  queue = queue.filter(q => q.id !== id);
  notify();
  item.resolve(value);
}

export function bpAlert(message: React.ReactNode, opts: Omit<AlertReq, 'kind' | 'message'> = {}): Promise<void> {
  return push({ kind: 'alert', message, ...opts });
}

export function bpConfirm(message: React.ReactNode, opts: Omit<ConfirmReq, 'kind' | 'message'> = {}): Promise<boolean> {
  return push({ kind: 'confirm', message, ...opts });
}

export function bpPrompt(message: React.ReactNode, opts: Omit<PromptReq, 'kind' | 'message'> = {}): Promise<string | null> {
  return push({ kind: 'prompt', message, ...opts });
}

function MessageBody({ message }: { message: React.ReactNode }) {
  if (typeof message === 'string') {
    return (
      <DialogDescription className="whitespace-pre-wrap">
        {message}
      </DialogDescription>
    );
  }
  return <div className="text-sm text-muted-foreground">{message}</div>;
}

function DialogItem({ req }: { req: Req }) {
  const [open, setOpen] = React.useState(true);
  const [value, setValue] = React.useState(req.kind === 'prompt' ? (req.defaultValue ?? '') : '');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (req.kind === 'prompt') {
      const t = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
      return () => clearTimeout(t);
    }
  }, [req.kind]);

  const close = (v: any) => {
    setOpen(false);
    setTimeout(() => settle(req.id, v), 100);
  };

  const onOpenChange = (next: boolean) => {
    if (next) return;
    if (req.kind === 'alert') close(undefined);
    else if (req.kind === 'confirm') close(false);
    else close(null);
  };

  const variant: Variant = req.kind === 'confirm' ? (req.variant ?? 'default') : 'default';
  const title =
    req.title ??
    (req.kind === 'alert' ? 'Notice' : req.kind === 'confirm' ? 'Confirm' : 'Input required');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {req.kind === 'prompt' ? (
            req.message ? <MessageBody message={req.message} /> : null
          ) : (
            <MessageBody message={req.message} />
          )}
        </DialogHeader>
        {req.kind === 'prompt' && (
          <form
            onSubmit={e => {
              e.preventDefault();
              close(value);
            }}
          >
            <Input
              ref={inputRef as any}
              autoFocus
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={req.placeholder}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  close(null);
                }
              }}
            />
          </form>
        )}
        <DialogFooter>
          {req.kind === 'alert' ? (
            <Button onClick={() => close(undefined)} autoFocus>
              {req.okLabel ?? 'OK'}
            </Button>
          ) : req.kind === 'confirm' ? (
            <>
              <Button variant="outline" onClick={() => close(false)}>
                {req.cancelLabel ?? 'Cancel'}
              </Button>
              <Button
                variant={variant === 'destructive' ? 'destructive' : 'default'}
                onClick={() => close(true)}
                autoFocus
              >
                {req.okLabel ?? 'OK'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => close(null)}>
                {req.cancelLabel ?? 'Cancel'}
              </Button>
              <Button onClick={() => close(value)}>{req.okLabel ?? 'OK'}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BpDialogHost() {
  const [reqs, setReqs] = React.useState<Req[]>(queue);
  React.useEffect(() => {
    listeners.add(setReqs);
    return () => {
      listeners.delete(setReqs);
    };
  }, []);
  return (
    <>
      {reqs.map(r => (
        <DialogItem key={r.id} req={r} />
      ))}
    </>
  );
}
